/**
 * Quotes on the Bid Desk — preview, PDF, Word, send, and the Q&A review desk.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Printer, FileType2, Send, MessageSquareQuote, ExternalLink, Loader2, Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import SendQuoteDialog from "@/components/admin/SendQuoteDialog";
import QuoteQuestionsDialog from "@/components/admin/QuoteQuestionsDialog";
import CoverPickerDialog from "@/components/admin/CoverPickerDialog";

const fmt$ = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const QuoteDeskPanel = ({ token }) => {
  const [quotes, setQuotes] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendFor, setSendFor] = useState(null);
  const [qaFor, setQaFor] = useState(null);
  const [coverFor, setCoverFor] = useState(null);

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [q, qa] = await Promise.all([
        axios.get(`${API}/admin/capital-quotes`, { headers }),
        axios.get(`${API}/admin/quote-questions`, { headers }),
      ]);
      setQuotes(q.data.quotes || []);
      const byQuote = {};
      for (const row of qa.data.questions || []) {
        byQuote[row.quote_id] = byQuote[row.quote_id] || { total: 0, waiting: 0, published: 0 };
        if (row.status !== "declined") byQuote[row.quote_id].total += 1;
        if (row.status === "awaiting_review") byQuote[row.quote_id].waiting += 1;
        if (row.status === "published") byQuote[row.quote_id].published += 1;
      }
      setCounts(byQuote);
    } catch {
      toast.error("Could not load the quote list");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading quotes…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="quote-desk-panel">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/70">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700">
          Contracted quotes
        </p>
        <h2 className="text-base font-bold text-slate-900">Quotes on the desk</h2>
        <p className="text-[12px] text-slate-500">
          Issue it, send it, and answer the questions that come back — every published answer
          becomes part of the quote.
        </p>
      </div>

      <div className="divide-y divide-slate-100">
        {quotes.map((q) => {
          const c = counts[q.id] || { total: 0, waiting: 0, published: 0 };
          return (
            <div key={q.id} className="p-4 hover:bg-slate-50/60" data-testid={`quote-row-${q.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-[260px]">
                  <p className="font-mono text-[11px] font-bold text-blue-700">{q.quote_number}</p>
                  <p className="text-sm font-bold text-slate-900 leading-snug">
                    {q.cover_headline || q.title}
                  </p>
                  <p className="text-[12px] text-slate-600">{q.title}</p>
                  <p className="text-[12px] text-slate-500">{q.subtitle || q.customer_name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Issued {q.issue_date || "—"} · complete {q.target_complete || "—"}
                    {q.status ? ` · ${q.status}` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-slate-900 tabular-nums">{fmt$(q.amount_total)}</p>
                  {!!q.bundle_discount && (
                    <p className="text-[11px] text-emerald-700">incl. {fmt$(q.bundle_discount)} discount</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => window.open(`${BACKEND}/api/capital-quotes/${q.id}/preview`, "_blank", "noopener")}
                  data-testid={`quote-preview-${q.id}`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => window.open(`${BACKEND}/api/capital-quotes/${q.id}/pdf`, "_blank", "noopener")}
                  data-testid={`quote-pdf-${q.id}`}>
                  <Printer className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => window.open(`${BACKEND}/api/capital-quotes/${q.id}/docx`, "_blank", "noopener")}
                  data-testid={`quote-docx-${q.id}`}>
                  <FileType2 className="w-3.5 h-3.5 mr-1" /> Word
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => setCoverFor(q)} data-testid={`quote-cover-${q.id}`}>
                  <ImageIcon className="w-3.5 h-3.5 mr-1" /> Cover
                </Button>
                <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                  onClick={() => setSendFor(q)} data-testid={`quote-send-${q.id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Send
                </Button>
                <Button size="sm" variant="outline"
                  className={`h-8 text-xs ${c.waiting ? "border-amber-400 bg-amber-50 text-amber-900" : ""}`}
                  onClick={() => setQaFor(q)} data-testid={`quote-qa-${q.id}`}>
                  <MessageSquareQuote className="w-3.5 h-3.5 mr-1" />
                  Questions{c.total ? ` (${c.total})` : ""}
                  {c.waiting ? ` · ${c.waiting} to review` : ""}
                </Button>
                {!!c.published && (
                  <button className="text-[11px] text-slate-500 underline hover:text-slate-800"
                    onClick={() => openAuthedDoc(`/quotes/${q.id}/addendum`, { token })}
                    data-testid={`quote-addendum-${q.id}`}>
                    Addendum PDF
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!quotes.length && (
          <p className="p-6 text-center text-sm text-slate-400">No contracted quotes yet.</p>
        )}
      </div>

      {sendFor && (
        <SendQuoteDialog quote={sendFor} token={token}
          onClose={() => setSendFor(null)} onSent={load} />
      )}
      {coverFor && (
        <CoverPickerDialog quote={coverFor} token={token}
          onClose={() => setCoverFor(null)} onApplied={load} />
      )}
      {qaFor && (
        <QuoteQuestionsDialog quote={qaFor} token={token}
          onClose={() => setQaFor(null)} onChanged={load} />
      )}
    </div>
  );
};

export default QuoteDeskPanel;
